import React, { createContext, useContext, ReactNode } from 'react';
import { useUserFavorites } from '../hooks/useUserFavorites';
import {
  UserFavorite,
  UserFavoriteInput,
  UserFavoriteFilter,
  UserFavoriteCheckResponse,
  UserFavoritePayload
} from '../types/userFavorites';
import { UserActivityEvent, ResourceInsights } from '../types/userActivity';

interface UserFavoritesContextType {
  favorites: UserFavorite[];
  loading: boolean;
  loadingMore: boolean;
  creating: boolean;
  updating: boolean;
  error: string | null;
  hasMore: boolean;
  continuationToken: string | null;
  
  // CRUD Operations
  createFavorite: (input: UserFavoriteInput) => Promise<UserFavorite>;
  updateFavorite: (id: string, payload: UserFavoritePayload) => Promise<UserFavorite>;
  deleteFavorite: (id: string) => Promise<void>;
  
  // Query Operations
  loadFavorites: (filter?: UserFavoriteFilter) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  getFavorite: (id: string) => Promise<UserFavorite>;
  
  // Check Operations
  checkFavorite: (kind: string, itemId: string) => Promise<UserFavoriteCheckResponse>;
  isFavorited: (kind: string, itemId: string) => boolean;
  
  // Count Operations
  getCount: (kind?: string) => Promise<number>;
  
  // Toggle Operations
  toggleFavorite: (kind: string, itemId: string, itemData?: Partial<UserFavoritePayload>) => Promise<{ isFavorited: boolean; favoriteId: string | null }>;
  toggleFavoriteFlag: (id: string) => Promise<{ isFavorited: boolean }>;
  
  // Filter Operations
  filterByKind: (kind: string) => UserFavorite[];
  
  // Helper methods
  getFavoriteById: (id: string) => UserFavorite | undefined;
  getFavoriteByItemId: (kind: string, itemId: string) => UserFavorite | undefined;
  getResourceByUrl: (url: string) => UserFavorite | undefined;
  
  // Activity tracking
  recordResourceVisit: (event: UserActivityEvent) => Promise<void>;
  activityEvents: UserActivityEvent[];
  insights: ResourceInsights;
  
  // State management
  clearError: () => void;
  clearFavorites: () => void;
}

interface UserFavoritesProviderProps {
  children: ReactNode;
}

const UserFavoritesContext = createContext<UserFavoritesContextType | undefined>(undefined);

export const UserFavoritesProvider: React.FC<UserFavoritesProviderProps> = ({ children }) => {
  const favoritesState = useUserFavorites();
  
  return (
    <UserFavoritesContext.Provider value={favoritesState}>
      {children}
    </UserFavoritesContext.Provider>
  );
};

export const useUserFavoritesContext = (): UserFavoritesContextType => {
  const context = useContext(UserFavoritesContext);
  if (!context) {
    throw new Error('useUserFavoritesContext must be used within a UserFavoritesProvider');
  }
  return context;
};
